import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, RefreshCw, Loader2, CheckCircle2, XCircle, MessageCircle,
  TrendingUp, Users, Phone, Mail, Globe, ChevronDown, MoveRight,
  Search, DollarSign, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Constantes ─────────────────────────────────────────────────────────── */

const STAGES = [
  { id: 'nuevo',        label: 'Nuevo',          color: '#64748B', bg: 'bg-slate-500/12', border: 'border-slate-500/25' },
  { id: 'calificado',   label: 'Calificado',      color: '#3B82F6', bg: 'bg-blue-500/12',  border: 'border-blue-500/25' },
  { id: 'pendiente_wa', label: 'Pendiente WA',    color: '#F59E0B', bg: 'bg-amber-500/12', border: 'border-amber-500/25' },
  { id: 'contactado',   label: 'Contactado',      color: '#8B5CF6', bg: 'bg-violet-500/12',border: 'border-violet-500/25' },
  { id: 'propuesta',    label: 'Propuesta',       color: '#10B981', bg: 'bg-emerald-500/12',border: 'border-emerald-500/25'},
];

const CLOSED_STAGES = [
  { id: 'cerrado_ganado',   label: 'Ganado',  color: '#22C55E' },
  { id: 'cerrado_perdido',  label: 'Perdido', color: '#EF4444' },
];

const SOURCE_ICON = { formulario: Mail, llamada: Phone, chat: MessageCircle };

const SCORE_COLOR = (s) => {
  if (s >= 75) return 'text-emerald-400';
  if (s >= 55) return 'text-amber-400';
  if (s >= 35) return 'text-blue-400';
  return 'text-slate-400';
};

const ALL_MOVE_STAGES = [...STAGES, ...CLOSED_STAGES];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

/* ── Quick Invoice Modal ─────────────────────────────────────────────────── */
function QuickInvoiceModal({ lead, onClose, onSave }) {
  const today = new Date(); today.setDate(today.getDate() + 30);
  const defaultDue = today.toISOString().split('T')[0];
  const [form, setForm] = useState({
    client_name: lead.name || '',
    client_phone: lead.phone || '',
    concept: 'Servicio Neuralia369',
    amount_usd: '',
    due_date_iso: defaultDue,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.amount_usd) return;
    onSave({ ...form, amount_usd: parseFloat(form.amount_usd), source_lead_id: lead.lead_id });
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="glass-card w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-text-heading font-semibold text-sm">Crear factura — {lead.name}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-text-muted" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] text-text-muted uppercase tracking-wider">Concepto</label>
            <input value={form.concept} onChange={e => set('concept', e.target.value)}
              className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-text-muted uppercase tracking-wider">Monto USD *</label>
              <input type="number" min="0" step="0.01" value={form.amount_usd} onChange={e => set('amount_usd', e.target.value)}
                className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-[10px] text-text-muted uppercase tracking-wider">Vencimiento</label>
              <input type="date" value={form.due_date_iso} onChange={e => set('due_date_iso', e.target.value)}
                className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary !py-2 text-xs">Cancelar</button>
            <button type="submit" className="flex-1 btn-primary !py-2 text-xs flex items-center justify-center gap-1">
              <DollarSign className="w-3 h-3" /> Crear factura
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

/* ── Lead Card ──────────────────────────────────────────────────────────── */
function LeadCard({ lead, stageId, onApprove, onReject, onMove, onInvoice, busy }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const Icon = SOURCE_ICON[lead.source] || Globe;
  const isWA = stageId === 'pendiente_wa';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="rounded-xl border border-border-subtle bg-card/60 p-3.5 space-y-2.5 relative"
      data-testid={`lead-card-${lead.lead_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
          <p className="text-xs font-semibold text-text-heading truncate">{lead.name}</p>
        </div>
        <span className={`text-xs font-black tabular-nums ${SCORE_COLOR(lead.score)}`}>{lead.score}</span>
      </div>

      <p className="text-[10px] text-text-muted leading-relaxed line-clamp-2">{lead.detail || '—'}</p>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {lead.country && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{lead.country}</span>
          )}
          <span className="text-[9px] text-text-muted">{fmtDate(lead.date)}</span>
        </div>

        {/* Move stage dropdown */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-0.5 text-[9px] text-text-muted hover:text-primary transition-colors"
            data-testid={`lead-move-btn-${lead.lead_id}`}
          >
            <MoveRight className="w-3 h-3" />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 bottom-full mb-1 w-36 rounded-xl border border-border-subtle bg-background shadow-xl z-20 overflow-hidden"
              >
                {ALL_MOVE_STAGES.filter(s => s.id !== stageId).map(s => (
                  <button
                    key={s.id}
                    onClick={() => { onMove(lead.lead_id, s.id); setMenuOpen(false); }}
                    className="w-full text-left px-3 py-2 text-[10px] hover:bg-card transition-colors"
                    style={{ color: s.color }}
                    data-testid={`move-to-${s.id}-${lead.lead_id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* WA Approve / Reject */}
      {isWA && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onApprove(lead.lead_id)}
            disabled={!!busy}
            className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold rounded-lg py-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
            data-testid={`approve-wa-${lead.lead_id}`}
          >
            {busy === lead.lead_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Enviar WA
          </button>
          <button
            onClick={() => onReject(lead.lead_id)}
            disabled={!!busy}
            className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold rounded-lg py-1.5 bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-colors disabled:opacity-50"
            data-testid={`reject-wa-${lead.lead_id}`}
          >
            <XCircle className="w-3 h-3" /> Rechazar
          </button>
        </div>
      )}

      {/* Facturar desde Propuesta */}
      {stageId === 'propuesta' && (
        <button
          onClick={() => onInvoice(lead)}
          className="w-full mt-1 flex items-center justify-center gap-1.5 text-[10px] font-semibold rounded-lg py-1.5 bg-amber-500/10 text-amber-400 border border-amber-500/25 hover:bg-amber-500/20 transition-colors"
          data-testid={`create-invoice-${lead.lead_id}`}
        >
          <DollarSign className="w-3 h-3" /> Crear factura
        </button>
      )}
    </motion.div>
  );
}

/* ── Panel principal ────────────────────────────────────────────────────── */
export default function AgenteVentasPanel({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState('');
  const [invoiceLead, setInvoiceLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      const res = await fn(`${API}/sales-agent/pipeline`);
      setData(await res.json());
    } catch (e) {
      toast.error('No se pudo cargar el pipeline');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const runAgent = async () => {
    setRunning(true);
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      const res = await fn(`${API}/sales-agent/run`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message || 'Agente ejecutado');
      await load();
    } catch {
      toast.error('Error al ejecutar el agente');
    } finally {
      setRunning(false);
    }
  };

  const approve = async (leadId) => {
    setApprovingId(leadId);
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      const res = await fn(`${API}/sales-agent/approve/${encodeURIComponent(leadId)}`, { method: 'POST' });
      const d = await res.json();
      if (d.wa_result === 'enviado') toast.success('WhatsApp enviado correctamente');
      else if (d.wa_result === 'sin_telefono') toast.warning('Lead sin número de teléfono — movido a Contactado sin WA');
      else toast.warning(`WA result: ${d.wa_result}`);
      await load();
    } catch {
      toast.error('Error al aprobar');
    } finally {
      setApprovingId(null);
    }
  };

  const reject = async (leadId) => {
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      await fn(`${API}/sales-agent/reject/${encodeURIComponent(leadId)}`, { method: 'POST' });
      toast.info('Lead movido de vuelta a Calificado');
      await load();
    } catch {
      toast.error('Error al rechazar');
    }
  };

  const moveStage = async (leadId, newStage) => {
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      await fn(`${API}/sales-agent/stage/${encodeURIComponent(leadId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage }),
      });
      toast.success('Lead movido');
      await load();
    } catch {
      toast.error('Error al mover lead');
    }
  };

  const handleCreateInvoice = async (payload) => {
    try {
      const fn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));
      const res = await fn(`${API}/cobros-agent/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (d.invoice_id) {
        toast.success(`Factura ${d.invoice_id} creada y vinculada al lead`);
        setInvoiceLead(null);
        await load();
      } else {
        toast.error('Error al crear factura');
      }
    } catch {
      toast.error('Error al crear factura');
    }
  };

  const stages = data?.stages || {};
  const stats = data?.stats || {};

  // Filtrar leads por búsqueda
  const filterLeads = (leads) => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.country || '').toLowerCase().includes(q) ||
      (l.detail || '').toLowerCase().includes(q)
    );
  };

  return (
    <div className="space-y-5" data-testid="agente-ventas-panel">
      {/* Quick Invoice Modal */}
      <AnimatePresence>
        {invoiceLead && (
          <QuickInvoiceModal
            lead={invoiceLead}
            onClose={() => setInvoiceLead(null)}
            onSave={handleCreateInvoice}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" /> Agente de Ventas
          </h3>
          <p className="text-sm text-text-muted mt-0.5">
            Pipeline automático de leads — scoring IA + aprobación humana para WhatsApp.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="btn-secondary !px-3 !py-2 text-xs flex items-center gap-1.5 disabled:opacity-50"
            data-testid="agente-ventas-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={runAgent}
            disabled={running || loading}
            className="btn-primary !px-4 !py-2 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="agente-ventas-run"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Ejecutar Agente
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar lead…"
          className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border-subtle rounded-xl text-text-body placeholder-text-muted focus:outline-none focus:border-primary"
          data-testid="ventas-search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Leads totales', value: stats.total ?? 0, icon: Users, color: 'text-primary' },
          { label: 'Pendientes WA', value: stats.pendientes_wa ?? 0, icon: MessageCircle, color: 'text-amber-400' },
          { label: 'Contactados', value: stats.contactados ?? 0, icon: CheckCircle2, color: 'text-violet-400' },
          { label: 'Propuestas', value: stats.propuestas ?? 0, icon: TrendingUp, color: 'text-emerald-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="glass-card p-4" data-testid={`av-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon className={`w-3.5 h-3.5 ${color}`} />
              <span className="text-[10px] text-text-muted uppercase tracking-wider">{label}</span>
            </div>
            <p className={`text-2xl font-black ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Kanban */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-text-muted">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando pipeline…
        </div>
      ) : (
        <div className="grid xl:grid-cols-5 gap-3 items-start" data-testid="av-kanban">
          {STAGES.map((st) => {
            const leads = stages[st.id] || [];
            return (
              <div key={st.id} className={`rounded-2xl border p-3 space-y-2.5 ${st.bg} ${st.border}`} data-testid={`kanban-col-${st.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: st.color }}>{st.label}</p>
                  <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-background/50 text-text-muted">{leads.length}</span>
                </div>
                <AnimatePresence>
                  {filterLeads(leads).map(lead => (
                    <LeadCard
                      key={lead.lead_id}
                      lead={lead}
                      stageId={st.id}
                      onApprove={approve}
                      onReject={reject}
                      onMove={moveStage}
                      onInvoice={setInvoiceLead}
                      busy={approvingId}
                    />
                  ))}
                </AnimatePresence>
                {filterLeads(leads).length === 0 && (
                  <p className="text-[10px] text-text-muted text-center py-4">Sin leads</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cerrado accordion */}
      <div className="rounded-2xl border border-border-subtle overflow-hidden">
        <button
          onClick={() => setShowClosed(!showClosed)}
          className="w-full flex items-center justify-between px-5 py-3.5 text-sm text-text-body hover:bg-card/40 transition-colors"
          data-testid="av-closed-toggle"
        >
          <span className="font-semibold">
            Cerrados — {(stages.cerrado_ganado?.length ?? 0) + (stages.cerrado_perdido?.length ?? 0)} leads
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showClosed ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {showClosed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
              className="overflow-hidden"
            >
              <div className="grid sm:grid-cols-2 gap-3 p-3">
                {CLOSED_STAGES.map(st => (
                  <div key={st.id} className="space-y-2" data-testid={`kanban-col-${st.id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: st.color }}>{st.label} ({(stages[st.id] || []).length})</p>
                    {(stages[st.id] || []).map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead} stageId={st.id} onApprove={approve} onReject={reject} onMove={moveStage} onInvoice={setInvoiceLead} busy={approvingId} />
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
